'use client';
import { useCallback, useMemo, useState } from 'react';

import { LinkButtonProps } from '@/components/atoms/LinkButton';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/components/atoms/LinkButton/LinkStrapi';
import { Toggle, ToggleProps } from '@/components/atoms/Toggle';

export type ToggleCMSProps = {
  toggles: {
    name: string;
    startsWith: number;
    endsWith: number;
    button?: LinkButtonStrapiProps;
    label: string;
  }[];
};

export function useCMSToggle<TList extends any[]>(
  list: TList,
  toggle?: ToggleCMSProps
) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const button = toggle?.toggles?.[selectedIndex]?.button;

  const getGroupedList = useMemo(() => {
    const togglesLength = toggle?.toggles.length || 0;

    const separatedList: TList[] = [];

    if (!togglesLength) {
      return separatedList;
    }

    for (let i = 0; i < togglesLength; i++) {
      const toggleItem = toggle!.toggles[i];
      const toggleList = list.slice(
        toggleItem.startsWith - 1,
        toggleItem.endsWith
      ) as TList;

      if (toggleList.length) {
        separatedList.push(toggleList);
      }
    }

    return separatedList;
  }, [list, toggle]);

  const toggleNames = useMemo(
    () => toggle?.toggles.map((toggle) => toggle.name),
    [toggle]
  );

  const toggleLabels = useMemo(
    () => toggle?.toggles.map((toggle) => toggle.label),
    [toggle]
  );

  const handleChange = useCallback((newIndex: number) => {
    setSelectedIndex(newIndex);
  }, []);

  const toggleActionButton = useCallback(
    ({ inner = {} }: { inner?: Partial<LinkButtonProps> }) => {
      if (!button) return null;

      return <LinkButtonStrapi block={button} inner={inner} />;
    },
    [button]
  );

  const toggleNode = useCallback(
    (toggleProps: Partial<ToggleProps> = {}) =>
      toggleNames ? (
        <Toggle
          {...toggleProps}
          labelBadge={toggleLabels}
          options={toggleNames}
          onValueChange={(newIndex) => handleChange(newIndex)}
          selectedIndex={selectedIndex}
        />
      ) : null,
    [toggleNames, toggleLabels, selectedIndex, handleChange]
  );

  if (!toggle)
    return {
      toggleNode: null,
      filteredToggleList: null,
    };

  return {
    toggleNode: toggleNode,
    filteredToggleList: getGroupedList[selectedIndex],
    ToggleActionButton: button ? toggleActionButton : undefined,
  };
}
